import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { logger } from '../utils/logger';
import { csrfService } from './csrf';
import { authCookieService } from './authCookies';

// Define the base URL for API requests
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important: Include cookies for CSRF
});

// Add request interceptor to include CSRF token
apiClient.interceptors.request.use(
  async (config) => {
    // Note: Auth token is now sent via httpOnly cookies automatically
    // with withCredentials: true, so no need to add Authorization header

    // Add CSRF token for state-changing requests
    if (csrfService.isStateChangingMethod(config.method)) {
      config.headers = await csrfService.addTokenToHeaders(config.headers as any);
    }

    // Don't override Content-Type if it's a FormData object
    if (config.data instanceof FormData && config.headers) {
      const headersWithDelete = config.headers as Record<string, any> & { delete?: (name: string) => void };
      if (typeof headersWithDelete.delete === 'function') {
        headersWithDelete.delete('Content-Type');
      } else {
        delete headersWithDelete['Content-Type']; // Let axios set the correct Content-Type with boundary
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle CSRF errors and auth token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if this is a CSRF error and we haven't already retried
    if (csrfService.isCsrfError(error) && !originalRequest._csrfRetry) {
      originalRequest._csrfRetry = true;
      logger.info('CSRF token invalid, fetching new token and retrying');

      try {
        // Fetch new CSRF token
        await csrfService.handleCsrfError();

        // Retry the original request with new token
        return apiClient.request(originalRequest);
      } catch (retryError) {
        logger.error('Failed to retry request after CSRF error', retryError);
        return Promise.reject(retryError);
      }
    }

    // Check if this is an auth error and we should refresh token
    if (authCookieService.shouldRefreshToken(error)) {
      originalRequest._retry = true;
      logger.info('Access token expired, refreshing and retrying');

      try {
        const refreshSuccess = await authCookieService.refreshAccessToken();
        if (refreshSuccess) {
          // Retry original request with new token (in cookie)
          return apiClient.request(originalRequest);
        }
      } catch (refreshError) {
        logger.error('Failed to refresh token', refreshError);
        // Token refresh failed, user needs to login again
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Types for API responses
export interface Platform {
  id: string;
  name: string;
}

export interface Segment {
  id: string;
  name: string;
}

export interface Sender {
  id: string;
  name: string;
  type: string;
}

export interface SimulationResponse {
  success: boolean;
  id?: string;
  simulationId?: string;
  data: {
    forecast: {
      positivePct: number;
      negativePct: number;
      neutralPct: number;
      score: number;
      confidence: string;
      driversPositive: string[];
      driversNegative: string[];
      eventType: string;
      baseline: string;
      delta: string;
    };
    simulation: Array<{
      tag: string;
      text: string;
    }>;
  };
  type: string;
  imageDescription?: {
    alt: string;
    notableDeviations: string[];
  };
}

export interface SimulationRequest {
  content: string;
  platform: string;
  sender: string;
  sector: string;
  accountId?: string;
  simCount: number;
  image?: File;
  imageDescription?: string;
  id?: string;
  isPrivate?: boolean;
  ideaDraftId?: string;
}

export interface IdeaGenerationRequest {
  brand: string;
  platform: string;
  icpId: string;
  topic: string;
  count?: number;
  sector: string;
  accountId?: string;
}

export interface IdeaGenerationResponse {
  success: boolean;
  data: {
    drafts: Array<{
      id: number;
      text: string;
      imageIdea?: string;
      rationale?: string;
      brandPulse?: any;
      brandPulseError?: string;
    }>;
  };
  type: string;
  ideaId: string;
  executionTime: string;
}

export interface DashboardSummary {
  kpis: {
    totalSimulations: number;
    totalIdeas: number;
    activeICPs: number;
    activeICPsLimit: number;
    socialAccounts: number;
    socialAccountsLimit: number;
    quotaUsed: number;
    quotaLimit: number;
    quotaResetDate: string | null;
  };
  trend: {
    labels: string[];
    positive: number[];
    negative: number[];
  };
  platformBreakdown: {
    labels: string[];
    counts: number[];
  };
  sentimentMix: {
    labels: string[];
    data: number[];
  };
  recentSimulations: Array<{
    id: string;
    content: string;
    createdAt: string;
    platform: string;
    sentiment: 'positive' | 'negative' | 'neutral';
    reputationDelta?: number;
  }>;
  recentIdeas: Array<{
    id: string;
    topic: string;
    brand: string;
    platform: string;
    createdAt: string;
    draftCount: number;
    reputationDelta?: number;
    reputation?: {
      uiScore: number;
      level: string;
    } | null;
  }>;
}

// Define a progress handler function type
export type ProgressHandler = (stepId: string, isCompleted: boolean) => void;

// Legacy alias for backward compatibility
export type SimulationProgressHandler = ProgressHandler;

// API service methods for wavescoop
const apiService = {
  // Initialize a simulation (save-first, async processing)
  initSimulation: async (
    data: SimulationRequest
  ): Promise<{ success: boolean; simulation: string; status: string }> => {
    const formData = new FormData();
    formData.append('content', data.content);
    formData.append('platform', data.platform);
    if (data.sender) formData.append('sender', data.sender);
    if (data.sector) formData.append('sector', data.sector);
    if (data.accountId) formData.append('accountId', data.accountId);
    if (typeof data.simCount !== 'undefined') formData.append('simCount', String(data.simCount));
    if (typeof data.isPrivate !== 'undefined') formData.append('isPrivate', String(data.isPrivate));
    if (data.image) formData.append('image', data.image);
    if (data.imageDescription) formData.append('imageDescription', data.imageDescription);
    if (data.ideaDraftId) formData.append('ideaDraftId', data.ideaDraftId);

    const response = await apiClient.post('/api/v1/simulations/init', formData);
    return response.data;
  },

  // Initialize an idea (save-first, async processing)
  initIdea: async (
    data: IdeaGenerationRequest & { count?: number }
  ): Promise<{ success: boolean; ideaId: string; status: string }> => {
    const response = await apiClient.post('/api/ideas/init', data);
    return response.data;
  },
  // Get available platforms
  getPlatforms: async (): Promise<Platform[]> => {
    const response = await apiClient.get('/api/platforms');
    return response.data.platforms;
  },

  // Get available audience segments
  getSegments: async (): Promise<Segment[]> => {
    const response = await apiClient.get('/api/segments');
    return response.data.segments;
  },

  // Get available senders
  getSenders: async (): Promise<Sender[]> => {
    const response = await apiClient.get('/api/senders');
    return response.data.senders;
  },

  // Get a simulation by ID
  getSimulation: async (id: string): Promise<any> => {
    const response = await apiClient.get(`/api/simulation/${id}`);
    return response.data;
  },

  // Opt-in for simulation completion email
  setSimulationNotify: async (id: string, notify: boolean): Promise<any> => {
    const response = await apiClient.patch(`/api/v1/simulations/${id}/notify`, { notify });
    return response.data;
  },

  // Generate ideas
  generateIdeas: async (
    data: IdeaGenerationRequest,
    progressCallback?: ProgressHandler
  ): Promise<IdeaGenerationResponse> => {
    // Generate a unique session ID for this idea generation
    const sessionId = uuidv4();
    
    // Set up SSE connection for progress updates if progressCallback is provided
    let eventSource: EventSource | null = null;

    if (progressCallback) {
      try {
        // Connect to SSE endpoint
        eventSource = new EventSource(`${API_BASE_URL}/api/ideas/generate/progress/${sessionId}`);
        
        // Listen for progress updates
        eventSource.onmessage = (event) => {
          try {
            const eventData = JSON.parse(event.data);
            
            // Check if this is a progress update
            if (eventData.stepId && typeof eventData.isCompleted === 'boolean') {
              progressCallback(eventData.stepId, eventData.isCompleted);
            }
            
            // Handle completion or error
            if (eventData.status === 'completed' || eventData.status === 'error') {
              eventSource?.close();
            }
          } catch (error) {
            logger.error('Error parsing SSE message:', error);
          }
        };
        
        // Error handling for SSE connection
        eventSource.onerror = () => {
          logger.error('SSE connection error');
          eventSource?.close();
        };
      } catch (error) {
        logger.error('Failed to establish SSE connection:', error);
      }
    }
    
    try {
      // Make the API request
      const response = await apiClient.post('/api/ideas/generate', {
        ...data,
        sessionId
      });
      
      // Clean up SSE connection if it exists
      if (eventSource) {
        eventSource.close();
      }
      
      return response.data;
    } catch (error) {
      // Clean up SSE connection if it exists
      if (eventSource) {
        eventSource.close();
      }
      throw error;
    }
  },

  // Opt-in for idea completion email
  setIdeaNotify: async (id: string, notify: boolean): Promise<any> => {
    const response = await apiClient.patch(`/api/ideas/${id}/notify`, { notify });
    return response.data;
  },

  // Wait until idea is visible (handles eventual consistency in prod)
  waitForIdea: async (id: string, opts?: { timeoutMs?: number; intervalMs?: number }): Promise<void> => {
    const timeoutMs = opts?.timeoutMs ?? 5000;
    const intervalMs = opts?.intervalMs ?? 300;
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      try {
        await apiClient.get(`/api/ideas/${id}/status`);
        return; // Found
      } catch (e: any) {
        const code = e?.response?.status;
        if (![404, 500, 503].includes(code)) throw e;
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    }
  },

  // Submit simulation request
  simulateReactions: async (
    data: SimulationRequest,
    progressCallback?: SimulationProgressHandler,
    navigate?: any // React Router navigate function
  ): Promise<SimulationResponse> => {
    // Generate a unique session ID for this simulation
    const sessionId = uuidv4();
    
    // Set up SSE connection for progress updates if onProgress is provided
    let eventSource: EventSource | null = null;
    
    // Track whether we've already processed a redirect
    let hasRedirected = false;

    if (progressCallback) {
      try {
        // Connect to SSE endpoint
        eventSource = new EventSource(`${API_BASE_URL}/api/simulate/progress/${sessionId}`);
        
        // Listen for progress updates
        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            // Check if this is a progress update
            if (data.stepId && typeof data.isCompleted === 'boolean') {
              progressCallback(data.stepId, data.isCompleted);
            }
            
            // Enhanced handling for completion event
            if (data.status === 'completed') {
              // Store the simulation ID in case we need it
              if (data.simulationId) {
                localStorage.setItem('lastSimulationId', data.simulationId);
                
                // If navigate function is provided, handle navigation
                if (navigate && typeof navigate === 'function' && !hasRedirected) {
                  hasRedirected = true;
                  logger.info('Navigating to results from SSE:', data.simulationId);
                  
                  // We'll make sure to save to localStorage first, then do a hard redirect
                  localStorage.setItem('lastSimulationId', data.simulationId);
                  
                  // Force hard redirect - this should be the most reliable approach
                  if (data.redirectUrl) {
                    window.location.href = data.redirectUrl;
                  } else {
                    window.location.href = `/simulations/${data.simulationId}/results`;
                  }
                }
              }
              
              eventSource?.close();
            }
            
            // Enhanced handling for error
            if (data.status === 'error') {
              logger.error('Simulation error via SSE:', data.message);
              
              // If navigate function is provided, handle navigation
              if (navigate && typeof navigate === 'function') {
                if (data.redirectUrl) {
                  navigate(data.redirectUrl);
                } else {
                  navigate('/dashboard');
                }
              }
              
              eventSource?.close();
            }
          } catch (error) {
            logger.error('Error parsing SSE message:', error);
          }
        };
        
        // Error handling for SSE connection
        eventSource.onerror = () => {
          logger.error('SSE connection error');
          eventSource?.close();
        };
      } catch (error) {
        logger.error('Failed to establish SSE connection:', error);
      }
    }
    
    // Create FormData for the simulation request
    const formData = new FormData();
    formData.append('content', data.content);
    formData.append('platform', data.platform);
    formData.append('sessionId', sessionId);
    
    // Add account ID if available
    if (data.accountId) {
      formData.append('accountId', data.accountId);
    } else {
      // Fall back to sender if accountId is not available
      formData.append('sender', data.sender);
    }
    
    // Add sector
    formData.append('sector', data.sector);
    
    // Add simulation type and count
    formData.append('simulationType', 'brand-pulse');
    formData.append('simCount', data.simCount.toString());
    
    // Add privacy setting
    formData.append('isPrivate', data.isPrivate ? 'true' : 'false');
    
    // Add editing information if available
    if (data.id) {
      formData.append('id', data.id);
    }
    
    // Add the image if available
    if (data.image) {
      formData.append('image', data.image);
    }
    // If no image file, but an image description was provided, send it
    if (!data.image && data.imageDescription) {
      formData.append('imageDescription', data.imageDescription);
    }
    if (data.ideaDraftId) {
      formData.append('ideaDraftId', data.ideaDraftId);
    }
    
    try {
      // The auth token will be added by the request interceptor
      const response = await apiClient.post('/api/simulate', formData);
      
      // Process the successful response
      const simulationData = response.data;
      
      // Store the simulation ID in localStorage as a fallback
      if (simulationData.simulation) {
        localStorage.setItem('lastSimulationId', simulationData.simulation);
        logger.info('Stored simulation ID from API response:', simulationData.simulation);
        
        // If navigate function is provided and we haven't already redirected via SSE, handle navigation
        if (navigate && typeof navigate === 'function' && !hasRedirected) {
          hasRedirected = true;
          logger.info('Navigating to results from API response:', simulationData.simulation);
          
          // We'll make sure to save to localStorage first, then do a hard redirect
          localStorage.setItem('lastSimulationId', simulationData.simulation);
          
          // Force hard redirect - this should be the most reliable approach
          window.location.href = `/simulations/${simulationData.simulation}/results`;
        }
      }
      
      // Clean up SSE connection if it exists
      if (eventSource) {
        eventSource.close();
      }
      
      return simulationData;
    } catch (error) {
      // Clean up SSE connection if it exists
      if (eventSource) {
        eventSource.close();
      }
      throw error;
    }
  },
  
  // Create checkout session for subscription
  createCheckoutSession: async (priceId: string, successUrl: string, cancelUrl: string): Promise<{url: string}> => {
    const response = await apiClient.post('/api/billing/checkout', {
      priceId,
      successUrl,
      cancelUrl
    });
    return response.data;
  },
  
  // Create billing portal session for managing subscriptions
  createBillingPortalSession: async (returnUrl: string): Promise<{url: string}> => {
    const response = await apiClient.get(`/api/billing/portal?returnUrl=${encodeURIComponent(returnUrl)}`);
    return response.data;
  },
  
  // Get subscription information
  getSubscriptionInfo: async (): Promise<any> => {
    // Auth check happens server-side via cookies
    const response = await apiClient.get('/api/billing/subscription');
    return response.data;
  },

  // Get available subscription plans
  getSubscriptionPlans: async (): Promise<any> => {
    // Try authenticated endpoint first, fall back to public if 401
    try {
      const response = await apiClient.get('/api/billing/plans');
      return response.data;
    } catch (error: any) {
      // If not authenticated, use public endpoint
      if (error?.response?.status === 401) {
        const response = await apiClient.get('/api/v1/public/plans');
        return response.data;
      }
      throw error;
    }
  },

  // Get dashboard summary data
  getDashboardSummary: async (): Promise<DashboardSummary> => {
    const response = await apiClient.get('/api/dashboard/summary');
    return response.data;
  },

  // Wait until simulation is visible
  waitForSimulation: async (id: string, opts?: { timeoutMs?: number; intervalMs?: number }): Promise<void> => {
    const timeoutMs = opts?.timeoutMs ?? 5000;
    const intervalMs = opts?.intervalMs ?? 300;
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      try {
        await apiClient.get(`/api/v1/simulations/${id}/status`);
        return; // Found
      } catch (e: any) {
        const code = e?.response?.status;
        if (![404, 500, 503].includes(code)) throw e;
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    }
  },

  // Get KPI data
  getKPIData: async (fromDate?: string, toDate?: string): Promise<any> => {
    const params = new URLSearchParams();
    if (fromDate) params.append('from', fromDate);
    if (toDate) params.append('to', toDate);
    
    const url = `/api/dashboard/kpis${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }
};

export default apiService;

/**
 * Utility function to safely navigate to simulation results
 * Can be called after receiving API response to ensure proper navigation
 * 
 * @param simulationData The API response from simulateReactions
 * @param navigate React Router navigate function
 */
export const navigateToSimulationResults = (simulationData: any, navigate: any) => {
  // Handle different formats of simulation ID in response
  const simulationId = 
    simulationData?.simulation || // direct ID
    simulationData?.id || // legacy ID field
    localStorage.getItem('lastSimulationId'); // fallback to last known ID
  
  if (simulationId) {
    logger.info('Navigating to simulation results using navigateToSimulationResults:', simulationId);
    
    // Save to localStorage first to ensure it's available
    localStorage.setItem('lastSimulationId', simulationId);
    
    // Force hard redirect - this should be the most reliable approach
    window.location.href = `/simulations/${simulationId}/results`;
  } else {
    logger.error('No simulation ID available for navigation');
    window.location.href = '/dashboard';
  }
}; 
